import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class WikiService {
  constructor(private router: Router, private location: Location) {}

  /**
   * Opens an app page in a new tab. prepareExternalUrl adds the base href,
   * so on the servers the link keeps its /application/ prefix.
   */
  private openInNewTab(urlTree: any): void {
    window.open(this.location.prepareExternalUrl(this.router.serializeUrl(urlTree)), '_blank');
  }

  /**
   * Formats a name into a lowercase URL slug (hyphen-separated).
   */
  formatSlug(name: string): string {
    if (!name) return '';
    return name
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  /**
   * Normalizes entity types to standard category names (e.g., 'biomes' -> 'biome', 'world-state' -> 'state').
   */
  private normalizeType(type: string): string {
    let t = (type || '').toLowerCase().replace(/^world-/, '');
    if (t.endsWith('s') && t !== 'politics') {
      t = t.slice(0, -1);
    }
    return t;
  }

  /**
   * Universal categories list.
   */
  private isUniversal(type: string): boolean {
    const universalTypes = [
      'biome', 'politics', 'alignment', 'geozone', 'race',
      'resource', 'class', 'industry', 'elevation', 'religion'
    ];
    return universalTypes.includes(type);
  }

  private storeActiveMapId(mapId?: any): void {
    if (!mapId) return;
    try {
      sessionStorage.setItem('activeWikiMapId', String(mapId));
      localStorage.setItem('activeWikiMapId', String(mapId));
    } catch (e) {}
  }

  /**
   * Generic method to open a wiki article in a new tab using the internal Angular Wiki module.
   */
  openWiki(type: string, value: string, mapId?: any, mapName?: string): void {
    if (!value) return;

    const normType = this.normalizeType(type);
    const slug = this.formatSlug(value);

    let urlTree;
    if (mapId && !this.isUniversal(normType)) {
      this.storeActiveMapId(mapId);
      urlTree = this.router.createUrlTree(['/wiki/world', mapId, normType, slug]);
    } else {
      urlTree = this.router.createUrlTree(['/wiki/universal', normType || 'biome', slug]);
    }

    this.openInNewTab(urlTree);
  }

  /**
   * Navigates to a wiki article within the current window/tab.
   */
  navigateToWiki(type: string, value: string, mapId?: any): void {
    if (!value) return;
    const normType = this.normalizeType(type);
    const slug = this.formatSlug(value);

    if (mapId && !this.isUniversal(normType)) {
      this.storeActiveMapId(mapId);
      this.router.navigate(['/wiki/world', mapId, normType, slug]);
    } else {
      this.router.navigate(['/wiki/universal', normType || 'biome', slug]);
    }
  }

  /**
   * Opens the wiki page for a specific map overview.
   */
  openMapWiki(mapId: any, mapName?: string): void {
    if (!mapId) return;
    this.storeActiveMapId(mapId);
    this.openInNewTab(this.router.createUrlTree(['/wiki/world', mapId]));
  }

  /**
   * Opens the wiki page for a city.
   */
  openCityWiki(cityName: string, mapId: any, mapName?: string): void {
    if (!cityName) return;
    const slug = this.formatSlug(cityName);
    const urlTree = mapId
      ? this.router.createUrlTree(['/wiki/world', mapId, 'city', slug])
      : this.router.createUrlTree(['/wiki/universal', 'city', slug]);

    this.openInNewTab(urlTree);
  }

  /**
   * Opens the wiki page for a resource.
   */
  openResourceWiki(resourceName: string, mapId?: any, mapName?: string): void {
    if (!resourceName) return;
    const slug = this.formatSlug(resourceName);
    this.openInNewTab(this.router.createUrlTree(['/wiki/universal', 'resource', slug]));
  }
}
